import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { FRAC_ZERO, type Fraction } from '@mimir/common/util/fractionType';
import { calculateRawAudioFrameBufferSize, calculateSampleCountForFrameNumber, rawAudioFrameIncompatibilityReason } from '@mimir/common/transcoding/rawFrame';
import type { AudioFrameV2, AudioInfoV2, BufferRange, FrameContext, FrameInfoV2, FrameProviderV2, FrameV2, Listener, PlaybackDirection, PreloadInfo, VideoFrameV2 } from '../frameProvider/frameProvider';
import { absFraction, addFractions, approximateFractionValue, compareFractions, divideFractions, floorFraction, frac, invertFraction, multiplyFractionBy, multiplyFractions, subtractFractions} from '@mimir/common/util/integerMath';
import { Guard } from '@mimir/common/async/async';
import { invariant } from '@mimir/common/util/invariant';
import type { Logger } from '@mimir/common/types/logger';
import { isAbortError } from '@mimir/common/util/abortControl';
import { exceptionToString } from '@mimir/common/util/exception';
import { asyncTimeout } from '@mimir/common/async/asyncTimeout';
import { abortify } from '@mimir/common/util/abortify';
import type { BufferCache, ReadonlyFloat32Array, ReadonlyInt16Array } from '@mimir/cutter/frameProvider/refCountedBuffer';
import { validateAudioFrame } from '@mimir/cutter/frameProvider/frameUtilities';

export interface AudioLevelsChannelData {
  level: number,
}

export interface AudioLevelsData {
  /**
   *  Max value to display (in dB)
   */
  max: number,

  /**
   *  Min value to display (in dB)
   */
  min: number,

  /**
   *  High value to display (in dB). This is the value where the audio level
   *  will start indicating that a value is considered "high".
   */
  high: number,

  /**
   *  Current values for each in channel (in db)
   */
  channels: AudioLevelsChannelData[],
}

export interface QueuedAudioFrame {
  audioFrame: AudioFrameV2;
  /**
   *  Will be called when the audio processor removes this audio frame from the queue
   */
  onAudioBufferDequeued: () => void;

  /**
   *  This is called when the audio frame is audible for the user (with various latency
   *  of the hardware taken into account. Will ALWAYS be called AFTER onAudioBufferDequeued.
   *  It will NEVER be called after onCancelledPlayout.
   */
  onAudioFrameStartPlaying: () => void;

  /**
   *  Will be called instead of onAudioFrameStartPlaying if the audio buffer is deleted
   *  without being played.
   */
  onCancelledPlayout: () => void;
}

export interface CutterCorePlayerTarget {
  displayVideoFrame(position: Fraction, videoFrame: VideoFrameV2 | null): void;
  displayAudioLevels(data: AudioLevelsData): void;
  queueAudioFrame(audioFrame: QueuedAudioFrame): void;
  startAudioPlayback(): void;
  stopAudioPlayback(clearQueue: boolean): void;
  setError(reason: unknown): void;
  onBufferedRangeChange(bufferedRanges: BufferRange[]): void;

  /**
   *  This is called when there are no pending commands in the player thread
   */
  playerIdle(): void;
  onFormatChanged(info: FrameInfoV2): void;
}

export interface PositionRequestAbsolute {
  type: 'absolute',
  position: Fraction,
}

export interface PositionRequestContextual {
  type: 'contextual',
  context: FrameContext,
}

export type PositionRequest = PositionRequestAbsolute | PositionRequestContextual;

export interface CutterCorePlayer {
  setPosition(position: PositionRequest): void;
  stepForward(): void;
  stepBackward(): void;
  setFrameProvider(provider: FrameProviderV2): void;
  /**
   *  Playback and seek will be clamped to [0, duration]
   */
  setDuration(duration: Fraction | null): void;
  setPlaybackRate(factor: Fraction): void;
  close(): void;
}

export function createPlayer(logger: Logger, target: CutterCorePlayerTarget, bufferCache: BufferCache) {
  return createPlayerPrivate(logger, target, bufferCache);
}

// ----

interface SetPositionCommand {
  type: 'set_position',
  request: PositionRequest,
  requestTime: number,
}

interface TerminateCommand {
  type: 'terminate',
}

interface SetFrameProviderCommand {
  type: 'set_frame_provider',
  provider: FrameProviderV2,
}

interface SetDurationCommand {
  type: 'set_duration',
  duration: Fraction | null,
}

interface StepForwardCommand {
  type: 'step_forward',
}

interface StepBackwardCommand {
  type: 'step_backward',
}

interface SetSpeedFactorCommand {
  type: 'set_speed_factor',
  factor: Fraction,
}

interface InvalidateCommand {
  type: 'invalidate';
}


type Command =
  SetPositionCommand |
  TerminateCommand |
  SetFrameProviderCommand |
  SetDurationCommand |
  StepForwardCommand |
  StepBackwardCommand |
  SetSpeedFactorCommand |
  InvalidateCommand;

const overriddenBySetPosition = new Set<Command['type']>([
  'set_position',
  'step_forward',
  'step_backward',
  'invalidate'
]);

function createChannelWithSetPositionReduction(): {
  poll: () => Promise<Command>,
  push: (command: Command) => void,
  count(): number,
} {
  const guard = new Guard();
  const queue : Command[] = [];
  let latestSetPositionCommand: Command | null = null;
  let latestSetDurationCommand: Command | null = null;
  let latestInvalidateCommand: Command | null = null;

  return {
    async poll(): Promise<Command> {
      while (true) {
        const [command] = queue.splice(0, 1);
        if (command === undefined){
          await guard.wait();
          continue;
        }

        if (command.type === 'invalidate') {
          if (latestInvalidateCommand === command) {
            latestInvalidateCommand = null;
          } else if (latestInvalidateCommand) {
            continue;
          }
          latestInvalidateCommand = command;
        }

        if (command.type === 'set_duration') {
          if (latestSetDurationCommand === command) {
            latestSetDurationCommand = null;
          } else if (latestSetDurationCommand) {
            continue;
          }
        }

        if (latestSetPositionCommand === command) {
          latestSetPositionCommand = null;
        } else if (latestSetPositionCommand && overriddenBySetPosition.has(command.type)) {
          /*
            We've already queued up a positioning command that will override
            this one, so there is not need to deliver this command.
          */
          continue;
        }

        return command;
      }
    },
    count(): number {
      return queue.length;
    },
    push(command: Command): void {
      /*
        If the current command is 'set_position' we want to remove previous
        commands that are redundant.
      */
      if (command.type === 'set_position') {
        while (queue.length > 0) {
          const lastType = queue[queue.length - 1].type;
          if (!overriddenBySetPosition.has(lastType)) {
            break;
          }
          queue.pop();
        }
        latestSetPositionCommand = command;
      }
      if (command.type === 'set_duration') {
        latestSetDurationCommand = command;
      }
      if (command.type === 'invalidate') {
        latestInvalidateCommand = command;
      }
      queue.push(command);
      guard.notify();
    },
  }
}

function createPlayerPrivate(logger: Logger, target: CutterCorePlayerTarget, bufferCache: BufferCache): CutterCorePlayer {
  const { poll, push, count } = createChannelWithSetPositionReduction();

  const listener: Listener = {
    onBufferedRangeChange(ranges: BufferRange[]): void {
      target.onBufferedRangeChange(ranges);
      // no-op
    },
    onInvalidate: function (): void {
      /*
        Reconstruct the position, so that we can push a set_position
        command to the player thread.
      */
      push({ type: 'invalidate' });


    },
    setDuration: function (duration: Fraction | null): void {
      push({ type: 'set_duration', duration });
    }
  };

  playerThread(poll, count, logger, target, listener, bufferCache);

  return {
    setPosition(request: PositionRequest): void {
      push({ type: 'set_position', request, requestTime: Date.now() });
    },
    setFrameProvider(provider: FrameProviderV2): void {
      push({ type: 'set_frame_provider', provider });
    },
    stepForward(): void {
      push({ type: 'step_forward' });
    },
    stepBackward(): void {
      push({ type: 'step_backward' });
    },
    close(): void {
      push({ type: 'terminate' });
    },
    setDuration(duration: Fraction | null): void {
      push({ type: 'set_duration', duration });
    },
    setPlaybackRate(factor: Fraction): void {
      push({ type: 'set_speed_factor', factor });
    },
  }
}

async function playerThread(
  pollCommand: () => Promise<Command>,
  pendingCommandsCount: () => number,
  logger: Logger,
  target: CutterCorePlayerTarget,
  listener: Listener,
  bufferCache: BufferCache,
) {
  const internal = new PlayerInternal(logger, target, listener, bufferCache);

  while (true) {
    if (pendingCommandsCount() === 0) {
      target.playerIdle();
    }
    const command = await pollCommand();
    logger.debug(`PlayerThread: received command: ${JSON.stringify(command)}`);
    try {
      switch (command.type) {
        case 'set_frame_provider': {
          await internal.setFrameProvider(command.provider);
          break;
        }
        case 'set_position': {
          await internal.setPosition(command.request, null);
          logger.info(`Seek time elapsed: ${Date.now() - command.requestTime}ms`);
          break;
        }
        case 'terminate': {
          await internal.terminate();
          return;
        }
        case 'set_duration': {
          internal.setDuration(command.duration);
          break;
        }
        case 'step_forward': {
          await internal.stepForwardOneFrame();
          break;
        }
        case 'step_backward': {
          await internal.stepBackwardOneFrame();
          break;
        }
        case 'set_speed_factor': {
          await internal.setPlaybackRate(command.factor);
          break;
        }
        case 'invalidate': {
          await internal.invalidate();
          break;
        }
      }
    } catch (error) {
      if (!isAbortError(error)) {
        target.setError(error);
      } else {
        logger.error(`Command aborted: ${command.type}`);
      }
    }
  }
}

function normalizedAmplitudeToDecibel(amplitude: number): number {
  if (amplitude === 0) {
    return -Infinity;
  }

  /*
    PCM amplitude values are root power quantities, unlike the power quantities
    Decibel is most commonly shown in relation to. The practical effect of this
    is that we multiply to log10 by 20 instead of 10.

    For more information see: https://en.wikipedia.org/wiki/Decibel
  */
  return 20 * Math.log10(amplitude);
}

function audioLevelDataFromFloat32Array(array: ReadonlyFloat32Array, channelCount: number) {
  const data: AudioLevelsData = {
    max: 0,
    min: -60,
    high: -5,
    channels: []
  };

  for (let i = 0; i < channelCount; i++) {
    let maxForChannel = 0;
    for (let j = i; j < array.length; j += channelCount) {
      maxForChannel = Math.max(maxForChannel, Math.abs(array[j]));
    }
    data.channels.push({ level: normalizedAmplitudeToDecibel(Math.min(1.0, maxForChannel)) });
  }
  return data;
}

function audioLevelDataFromInt16Array(array: ReadonlyInt16Array, channelCount: number) {
  const data: AudioLevelsData = {
    max: 0,
    min: -60,
    high: -5,
    channels: []
  };

  for (let i = 0; i < channelCount; i++) {
    let maxForChannel = 0;
    for (let j = i; j < array.length; j += channelCount) {
      maxForChannel = Math.max(maxForChannel, Math.abs(array[j]));
    }
    const maxInt16 = 32767;
    data.channels.push({ level: normalizedAmplitudeToDecibel(Math.min(1.0, maxForChannel / maxInt16)) });
  }
  return data;
}

function audioLevelDataFromFrame(audioFrame: AudioFrameV2 | null): AudioLevelsData {
  if (audioFrame === null) {
    return {
      max: 0,
      min: -60,
      high: -5,
      channels: []
    };
  }

  switch (audioFrame.format) {
    case 'float32': {
      const array = audioFrame.buffer.asReadonlyFloat32Array();
      return audioLevelDataFromFloat32Array(array, audioFrame.channelCount);
    }
    case 'pcm16': {
      const array = audioFrame.buffer.asReadonlyInt16Array();
      return audioLevelDataFromInt16Array(array, audioFrame.channelCount);
    }
  }
}

const NO_ABORT = (new AbortController()).signal;

interface FrameFetching {
  abortController: AbortController,
  promise: Promise<void>,
}

interface FrameNumberRequestAbsolute {
  type: 'absolute',
  frameNumber: number,
}

interface FrameNumberRequestContextual {
  type: 'contextual',
  context: FrameContext,
}

type FrameNumberRequest = FrameNumberRequestAbsolute | FrameNumberRequestContextual;

function positionRequestToFrameNumberRequest(
  request: PositionRequest,
  frameRate: Fraction,
): FrameNumberRequest {
  switch (request.type) {
    case 'absolute': {
      const frameNumber = approximatePositionToFrameNumber(request.position, frameRate);
      return { type: 'absolute', frameNumber};
    }
    case 'contextual': {
      return request;
    }
  }
}

interface CurrentPosition {
  position: Fraction,
  context: FrameContext | null,
}

class PlayerInternal {
  frameFetching: FrameFetching | null = null;
  audioPlaybackStarted: boolean = false;
  active: { frameProvider: FrameProviderV2 } | null = null;
  currentFramePosition: CurrentPosition = { position: FRAC_ZERO, context: null };
  currentRequestedPosition: PositionRequest | null = null;
  duration: Fraction | null = null;
  prerollComplete: boolean = false;
  requestedPlaying : boolean = false;
  playbackRate : Fraction = frac(1, 1);
  info: FrameInfoV2 | null = null;

  public constructor(
    private logger: Logger,
    private target: CutterCorePlayerTarget,
    private readonly listener: Listener,
    private bufferCache: BufferCache,
  ) { }

  public async setFrameProvider(frameProvider: FrameProviderV2) {
    await this.abortPlayback();

    if (this.active !== null) {
      this.active.frameProvider.setListener(null);
      this.active.frameProvider.close();
    }

    this.logger.info('PlayerThread: updating frame provider');
    this.active = { frameProvider };
    this.active.frameProvider.setListener(this.listener);
  }

  async getFrameInfo(): Promise<FrameInfoV2> {
    // NOTE: Don't be tempted to cache the info here. We need to always fetch
    // it in case the frame provider has changed format.
    invariant(this.active !== null, 'No active frame provider');
    const info = await this.active.frameProvider.getInfo(NO_ABORT);
    if ((this.info === null) || (!isEqual(this.info, info))) {
      this.info = info;
      this.target.onFormatChanged(this.info);
    }
    return this.info;
  }

  async getFrameRate(): Promise<Fraction> {
    const info = await this.getFrameInfo();
    return info.frameRate;
  }

  private async setPositionInternal(request: PositionRequest, preloadInfo: PreloadInfo | null) {
    if (this.active === null) {
      this.logger.debug('no frame provider active');
      return;
    }

    const frameRate = await this.getFrameRate();

    const frameNumberRequest = positionRequestToFrameNumberRequest(request, frameRate);

    this.requestedPlaying = false;

    await this.seekToFrameNumberAndStartPreroll(frameNumberRequest, preloadInfo);
  }

  public async setPosition(request: PositionRequest, preloadInfo: PreloadInfo | null) {
    if (isEqual(this.currentRequestedPosition, request)) {
      this.logger.debug('ignoring setPosition request since it is the same as the previous requested position');
      return;
    }

    this.currentRequestedPosition = cloneDeep(request);
    await this.setPositionInternal(request, preloadInfo);
  }


  public async terminate() {
    await this.abortPlayback();
  }

  public setDuration(duration: Fraction | null) {
    this.duration = duration;
  }

  public async stepForwardOneFrame() {
    await this.stepFrames(1);
  }

  public async stepBackwardOneFrame() {
    await this.stepFrames(-1);
  }

  public async setPlaybackRate(speedFactor: Fraction) {
    if (speedFactor.numerator === 0) {
      this.target.stopAudioPlayback(false);
      this.audioPlaybackStarted = false;
      this.requestedPlaying = false;
      // Note: We explicitly never set the playback rate to zero. Instead we
      // remember the last value so that we can preroll in the expected
      // direction.
    } else {
      this.requestedPlaying = true;
      this.playbackRate = speedFactor;
      if (this.frameFetching === null && this.active != null) {
        const info = await this.getFrameInfo();
        const frameNumber = approximatePositionToFrameNumber(this.currentFramePosition.position, info.frameRate);
        await this.startFrameFetching(frameNumber, info, 0);
      }
      this.startPlayingIfApplicable();
    }
  }

  public async invalidate() {
    if (!this.audioPlaybackStarted) {
      if (this.currentFramePosition.context !== null) {
        await this.setPositionInternal({ type: 'contextual', context: this.currentFramePosition.context }, null);
      } else {
        await this.setPositionInternal({ type: 'absolute', position: this.currentFramePosition.position }, null);
      }
    }
  }


  // ----

  private async stepFrames(delta: number) {
    await this.abortPlayback();
    this.requestedPlaying = false;
    const frameRate = await this.getFrameRate();
    const newPosition = addFractions(this.currentFramePosition.position, multiplyFractionBy(invertFraction(frameRate), delta));
    const preloadInfo : PreloadInfo = {
      direction: (delta < 0) ? 'backward' : 'forward',
      clampToRange: null,
    };
    await this.setPositionInternal({ type: 'absolute', position: newPosition }, preloadInfo);
  }

  private getFrameCountFromFrameRate(frameRate: Fraction): number | null {
    if (this.duration === null) {
      return null;
    }
    const frameCountFraction = multiplyFractions(this.duration, frameRate);
    return Math.round(approximateFractionValue(frameCountFraction));
  }

  private async getFrameCount(): Promise<number | null> {
    if (this.active === null) {
      return null;
    }

    const info = await this.active.frameProvider.getInfo(NO_ABORT);
    return this.getFrameCountFromFrameRate(info.frameRate);
  }

  private async fetchFrame(request: FrameNumberRequest, preloadInfo: PreloadInfo | null): Promise<FrameV2 | null> {
    if (this.active === null) {
      this.logger.debug('no frame provider active');
      return null;
    }

    switch (request.type) {
      case 'absolute': {
        const frameCount : number | null = await this.getFrameCount();
        const currentFrameNumber = clampFrameNumber(request.frameNumber, frameCount);

        this.logger.info(`Seek to frame: ${currentFrameNumber}`);

        return await this.active.frameProvider.getFrame(currentFrameNumber, preloadInfo, NO_ABORT);
      }
      case 'contextual': {
        const frame = await this.active.frameProvider.getFrameByContext(request.context, NO_ABORT);
        if (frame === null) {
          this.logger.warn(`Unable to seek to contextual frame: ${JSON.stringify(request.context)}`);
          return null;
        }
        return frame;
      }
    }
  }

  private async seekToFrameNumberAndStartPreroll(request: FrameNumberRequest, preloadInfo: PreloadInfo | null) {
    await this.abortPlayback();

    const frame = await this.fetchFrame(request, preloadInfo);

    if (frame === null) {
      return null;
    }
    const frameNumberAsFraction : Fraction = {
      numerator: frame.frameNumber,
      denominator: 1
    };
    const info = sanitizeFrameInfoV2(frame);
    const frameRate = info.frameRate;
    const exactPosition : Fraction = divideFractions(frameNumberAsFraction, frameRate);

    this.currentFramePosition = {
      position: exactPosition,
      context: request.type === 'contextual' ? request.context : null,
    };

    this.logger.debug('Player: schedule first audio frame, has video: ' + (frame.video !== null));
    this.target.displayVideoFrame(exactPosition, frame.video);
    this.target.displayAudioLevels(audioLevelDataFromFrame(frame.audio));

    if (frame.audio !== null) {
      this.target.queueAudioFrame({
        audioFrame: frame.audio,
        onAudioBufferDequeued: () => { },
        onAudioFrameStartPlaying: () => { },
        onCancelledPlayout: () => { }
      });
    }

    // We wait give the frame fetching a 200 ms in order to avoid preload
    // during seeking
    await this.startFrameFetching(frame.frameNumber, info, 200);
  }

  private async startFrameFetching(startFrameNumber: number, info: FrameInfoV2, startDelayMs: number) {
    if (!this.active) {
      return;
    }

    this.prerollComplete = false;

    const frameRate = info.frameRate;

    const videoQueue = createVideoQueue(frameRate);
    const audioQueue = createAudioQueue(this.bufferCache);

    const abortController = new AbortController();
    const promise =
      frameFetching({
        logger: this.logger,
        target: this.target,
        frameRate,
        provider: this.active.frameProvider,
        abortSignal: abortController.signal,
        startFrameNumber,
        info,
        startDelayMs,
        getPlaybackRate: () => {
          return this.playbackRate;
        },
        onStopped: (reason: FetchStopReason) => {
          this.target.stopAudioPlayback(false);
          this.audioPlaybackStarted = false;
          if (reason === 'format-change') {
            this.invalidate().catch((error) => {
              console.error('Failed to set position after stopping playback', exceptionToString(error));
            });
          }
        },
        getFrameCount: () => {
          return this.getFrameCountFromFrameRate(frameRate);
        },
        prerollCount: Math.round(approximateFractionValue(frameRate) * 0.120),
        onPreRollComplete: () => {
          this.prerollComplete = true;
          this.startPlayingIfApplicable();
        },
        onFramePresented: (framePostion: Fraction) => {
          this.currentFramePosition = { position: framePostion, context: null };
          this.currentRequestedPosition = null;
        },
        bufferCache: this.bufferCache,
        videoQueue,
        audioQueue,
      }).catch((error) => {
        if (!isAbortError(error)) {
          this.target.setError(error);
          this.logger.error(`Frame fetching thread failed unexpectedly: ${exceptionToString(error)}`);
        }
      }).finally(() => {
        this.frameFetching = null;
        videoQueue.close();
        audioQueue.close();
      });

    this.frameFetching = { abortController, promise };
  }

  private async abortPlayback(clearQueue: boolean = true) {
    if (this.frameFetching) {
      this.frameFetching.abortController.abort();
      await this.frameFetching.promise;
      this.frameFetching = null;
    }
    this.target.stopAudioPlayback(clearQueue);
    this.audioPlaybackStarted = false;
  }

  private startPlayingIfApplicable() {
    if (this.audioPlaybackStarted) {
      this.logger.silly('PlayerThread: start playback ignored (already playing)');
      return;
    }

    if (!this.requestedPlaying) {
      this.logger.silly('PlayerThread: start playback ignored (play not requested)');
      return;
    }

    if (!this.prerollComplete) {
      this.logger.debug('PlayerThread: start playback delayed (pre roll not complete)');
      return;
    }

    this.target.startAudioPlayback();

    this.audioPlaybackStarted = true;
  }

}

type FetchStopReason = 'format-change' | 'missing-frame' | 'error';

interface FrameFetchingParameters {
  logger: Logger,
  provider: FrameProviderV2,
  target: CutterCorePlayerTarget,
  startFrameNumber: number,
  getFrameCount: () => number | null,
  getPlaybackRate: () => Fraction,
  prerollCount: number,
  abortSignal: AbortSignal,
  frameRate: Fraction,
  info: FrameInfoV2,
  onPreRollComplete: () => void,
  onFramePresented: (position: Fraction) => void,
  onStopped: (reason: FetchStopReason) => void,
  bufferCache: BufferCache,
  startDelayMs: number,
  videoQueue: VideoQueue,
  audioQueue: AudioQueue,
}

function approximatePositionToFrameNumber(approximatePosition: Fraction, frameRate: Fraction) {
  return floorFraction(multiplyFractions(approximatePosition, frameRate));
}

async function frameFetching(parameters: FrameFetchingParameters) {
  const {
    logger,
    provider,
    target,
    prerollCount,
    getFrameCount,
    getPlaybackRate,
    info,
    frameRate,
    abortSignal,
    onFramePresented,
    onStopped,
    bufferCache,
    startDelayMs,
    audioQueue,
    videoQueue,
  } = parameters;

  if (startDelayMs > 0) {
    await abortify(abortSignal, () => asyncTimeout(startDelayMs));
  }

  const stats = { time: 0, count: 0, high: 0, values: [] as number[] };

  const maxScheduledFrames = prerollCount;

  let prerollComplete = false;
  let frameNumber = parameters.startFrameNumber;
  let pendingFramesCount = 0;
  let scheduledAudioFrameDequeued = new Guard();

  let playbackDirection : PlaybackDirection =
    (compareFractions(getPlaybackRate(), FRAC_ZERO) < 0) ? 'backward' : 'forward';

  await provider.preload(frameNumber, { direction: playbackDirection, clampToRange: null }, abortSignal);

  abortSignal.addEventListener('abort', () => scheduledAudioFrameDequeued.notify());

  logger.debug(`FrameFetching: started at frame ${frameNumber}`);

  let outputFrameNumber = 0;
  let stopReason: FetchStopReason | null = null;


  const signalStopIfDrained = () => {
    if (stopReason && pendingFramesCount === 0) {
      onStopped(stopReason);
    }
  };

  const stopWhenDrained = (reason: FetchStopReason) => {
    stopReason = reason
    signalStopIfDrained();
  };

  while (true) {
    if ((pendingFramesCount >= prerollCount) && !prerollComplete) {
      prerollComplete = true;
      logger.debug(`FrameFetching: pre-roll complete. Waiting for frame to be presented`);
      parameters.onPreRollComplete();
    }

    while (pendingFramesCount >= maxScheduledFrames) {
      await scheduledAudioFrameDequeued.wait();
      if (abortSignal.aborted) {
        return;
      }
    }

    const frameCount = getFrameCount();
    if ((frameCount !== null) && (frameNumber >= frameCount)) {
      logger.debug(`FrameFetching: reached end. frameCount=${frameCount}, position=${frameNumber}`);
      stopWhenDrained('missing-frame');
      return;
    }
    if (frameNumber < 0) {
      stopWhenDrained('missing-frame');
      return;
    }

    const getFrameStart = Date.now();
    performance.mark('start-frame');
    const frame = await provider.getFrame(frameNumber, { direction: playbackDirection, clampToRange: null }, abortSignal)
    const getFrameEnd = Date.now();
    const getFrameElapsed = getFrameEnd - getFrameStart;
    performance.mark('end-frame');
    performance.measure('frame-duration', 'start-frame', 'end-frame');
    stats.time += getFrameElapsed;
    stats.high = Math.max(stats.high, getFrameElapsed);
    stats.values.push(getFrameElapsed);
    stats.count ++;
    if (stats.count % 100 === 0) {
      stats.values.sort((a, b) => a - b);
      const mean = stats.values[stats.values.length / 2];
      logger.debug(`Player getFrame average elapsed time: ${stats.time / stats.count} ms (high ${stats.high} ms, mean ${mean} ms)`);
      stats.count = 0;
      stats.time = 0;
      stats.high = 0;
      stats.values = [];
    }

    if (compareFractions(frame.frameRate, info.frameRate) !== 0) {
      logger.warn(`FrameFetching: playback was interrupted due to change in frame rate: ${approximateFractionValue(frame.frameRate)} != ${approximateFractionValue(info.frameRate)}`);
      stopWhenDrained('format-change');
      return;
    }

    if ((frame.audio?.channelCount ?? 0) !== (info.audio?.channelCount ?? 0)) {
      logger.warn(`FrameFetching: playback was interrupted due to change number of channels: ${frame.audio?.channelCount ?? 0} != ${frame.audio?.channelCount ?? 0}`);
      stopWhenDrained('format-change');
      return;
    }

    if (frame.frameNumber !== frameNumber) {
      const message = `FrameFetching: FrameProvider provided incorrect frame number (${frame.frameNumber}) when ${frameNumber} was requested`;
      target.setError(new Error(message));
      logger.error(message);
      stopWhenDrained('error');
      return;
    }

    if (abortSignal.aborted) {
      return;
    }

    invariant(frame.audio !== null, 'Video-only not implemented');

    const inputVideoFrame = frame.video;
    let inputAudioFrame = frame.audio;

    if (playbackDirection === 'backward') {
      inputAudioFrame = reverseAudioFrame(inputAudioFrame, bufferCache);
    }

    const position = divideFractions({ numerator: frameNumber, denominator: 1 }, frameRate);

    videoQueue.push(inputVideoFrame);
    audioQueue.push(inputAudioFrame);

    const absPlaybackRate = absFraction(getPlaybackRate());
    const frameDuration = invertFraction(frameRate);
    const sourceFrameDuration = multiplyFractions(frameDuration, absPlaybackRate);

    while (true) {
      if (compareFractions(videoQueue.duration(), sourceFrameDuration) < 0) {
        break;
      }

      const inputSampleCount = calculateSampleCountForFrameNumber(inputAudioFrame.sampleRate, divideFractions(frameRate, absPlaybackRate), outputFrameNumber)

      if (audioQueue.sampleCount() < inputSampleCount) {
        break;
      }

      const outputVideoFrame = videoQueue.take(sourceFrameDuration);
      let outputAudioFrame = audioQueue.take(inputSampleCount);

      outputFrameNumber++;

      const outputSampleCount = calculateSampleCountForFrameNumber(outputAudioFrame.sampleRate, frameRate, outputFrameNumber);

      outputAudioFrame = stretchAudioFrame(outputAudioFrame, outputSampleCount, bufferCache);

      const audioLevels = audioLevelDataFromFrame(outputAudioFrame);

      target.queueAudioFrame({
        audioFrame: outputAudioFrame,
        onAudioFrameStartPlaying: () => {
          if (!abortSignal.aborted) {
            target.displayVideoFrame(position, outputVideoFrame);
            target.displayAudioLevels(audioLevels);
            onFramePresented(position);
          }
        },
        onAudioBufferDequeued: () => {
          if (!abortSignal.aborted) {
            pendingFramesCount -= 1;
            signalStopIfDrained();
            scheduledAudioFrameDequeued.notify();
          }
        },
        onCancelledPlayout: () => {
          outputVideoFrame?.buffer.close();
        }
      });
      pendingFramesCount += 1;
    }


    const nextPlaybackDirection : PlaybackDirection =
      (compareFractions(getPlaybackRate(), FRAC_ZERO) < 0) ? 'backward' : 'forward';

    // If we have changed playback direction we want to play the same audio
    // frame again, but in reverse direction so we don't change the frame
    // number.
    if (nextPlaybackDirection === playbackDirection) {
      if (nextPlaybackDirection === 'backward') {
        frameNumber -= 1;
      } else {
        frameNumber += 1;
      }
    } else {
      logger.info('Direction changed. Triggering preload..');
      await provider.preload(frameNumber, { direction: nextPlaybackDirection, clampToRange: null }, abortSignal);
    }
    playbackDirection = nextPlaybackDirection;
  }
}

function reverseAudioFrame(audioFrame: AudioFrameV2, bufferCache: BufferCache): AudioFrameV2 {
  const reversedBuffer =
    bufferCache.allocateBuffer(
      audioFrame.buffer.size(),
      (buffer: Uint8Array) => {
        switch (audioFrame.format) {
          case 'pcm16': {
            const source = audioFrame.buffer.asReadonlyInt16Array();
            const target = new Int16Array(buffer.buffer);
            let srcOffset = 0;
            let dstOffset = (audioFrame.sampleCount - 1) * audioFrame.channelCount;
            for (let i = 0; i < audioFrame.sampleCount; i++) {
              for (let j = 0; j < audioFrame.channelCount; j++) {
                target[dstOffset + j] = source[srcOffset + j];
              }
              srcOffset += audioFrame.channelCount;
              dstOffset -= audioFrame.channelCount;
            }
          }
          case 'float32': {
            const source = audioFrame.buffer.asReadonlyFloat32Array();
            const target = new Float32Array(buffer.buffer);
            let srcOffset = 0;
            let dstOffset = (audioFrame.sampleCount - 1) * audioFrame.channelCount;
            for (let i = 0; i < audioFrame.sampleCount; i++) {
              for (let j = 0; j < audioFrame.channelCount; j++) {
                target[dstOffset + j] = source[srcOffset + j];
              }
              srcOffset += audioFrame.channelCount;
              dstOffset -= audioFrame.channelCount;
            }
          }
        }
      }
    );

  audioFrame.buffer.close();

  return {
    sampleOffset: audioFrame.sampleOffset,
    sampleCount: audioFrame.sampleCount,
    sampleRate: audioFrame.sampleRate,
    channelCount: audioFrame.channelCount,
    format: audioFrame.format,
    buffer: reversedBuffer
  }
}

function clampFrameNumber(frameNumber: number, frameCount: number | null): number {
  frameNumber = Math.max(frameNumber, 0);
  if (frameCount !== null) {
    frameNumber = Math.min(frameNumber, frameCount - 1);
  }
  return frameNumber;
}

function sanitizeFrameInfoV2(info: FrameInfoV2): FrameInfoV2 {
  return {
    frameRate: info.frameRate,
    video: info.video ? {
      resolution: info.video.resolution,
      pixelFormat: info.video.pixelFormat,
      fieldOrder: info.video.fieldOrder,
    } : null,
    audio: info.audio ? {
      sampleRate: info.audio.sampleRate,
      channelCount: info.audio.channelCount,
      format: info.audio.format,
    } : null,
  };
}

interface VideoQueue {
  push(frame: VideoFrameV2 | null): void;
  duration(): Fraction;
  take(frameDuration: Fraction): VideoFrameV2 | null;
  close(): void;
}

function createVideoQueue(frameRate: Fraction): VideoQueue {
  const queue : (VideoFrameV2 | null)[] = [];

  const frameDuration = invertFraction(frameRate);
  let offset : Fraction = FRAC_ZERO;

  return {
    push(frame: VideoFrameV2 | null): void {
      queue.push(frame);
    },
    duration(): Fraction {
      return subtractFractions(multiplyFractionBy(frameDuration, queue.length), offset);
    },
    take(outputFrameDuration: Fraction): VideoFrameV2 | null {
      while (compareFractions(offset, frameDuration) > 0) {
        const [frame] = queue.splice(0, 1);
        frame?.buffer.close();
        offset = subtractFractions(offset, frameDuration);
      }
      offset = addFractions(offset, outputFrameDuration);
      invariant(queue.length > 0, 'VideoQueue empty');
      const frame = queue[0];
      if (frame === null) {
        return null;
      }
      return {
        resolution: frame.resolution,
        pixelFormat: frame.pixelFormat,
        fieldOrder: frame.fieldOrder,
        buffer: frame.buffer.addReference(),
      };
    },
    close(): void {
      queue.splice(0).forEach((videoFrame) => videoFrame?.buffer.close());
    }
  }
}

interface AudioQueue {
  push(frame: AudioFrameV2): void;
  sampleCount(): number;
  take(sampleCount: number): AudioFrameV2;
  close(): void;
}

function createAudioQueue(bufferCache: BufferCache): AudioQueue {
  let info : AudioInfoV2 | null = null;
  const queue : AudioFrameV2[] = [];
  let queueByteOffset : number = 0;

  let sampleCountInCache = 0;
  let sampleOffset = 0;

  return {
    push(frame: AudioFrameV2): void {
      sampleCountInCache += frame.sampleCount;
      validateAudioFrame(frame);
      queue.push(frame);
      if (info !== null) {
        const incompatibilityReason =
          rawAudioFrameIncompatibilityReason(info, frame);

        if (incompatibilityReason !== null) {
          throw new Error(`Audio format changed: ${incompatibilityReason}`);
        }
      } else {
        info = {
          sampleRate: frame.sampleRate,
          channelCount: frame.channelCount,
          format: frame.format,
        };
      }
    },
    sampleCount(): number {
      return sampleCountInCache;
    },
    take(sampleCount: number): AudioFrameV2 {
      invariant(info !== null, 'AudioQueue is empty');
      const audioBufferSize = calculateRawAudioFrameBufferSize(info, sampleCount);
      const buffer =
        bufferCache.allocateBuffer(
          audioBufferSize,
          (buffer: Uint8Array) => {
            let dstOffset = 0;
            while (dstOffset < buffer.length) {
              invariant(queue.length > 0, 'AudioQueue is empty');
              const srcBuffer = queue[0].buffer;
              const firstBuffer = srcBuffer;
              const srcOffset = queueByteOffset;
              const remain = buffer.length - dstOffset;
              const srcSize = Math.min(remain, firstBuffer.size() - srcOffset);
              const src = srcBuffer.asReadonlyUint8Array();

              for (let i = srcOffset; i < srcOffset + srcSize; i++) {
                buffer[dstOffset++] = src[i];
              }
              if (srcOffset + srcSize === srcBuffer.size()) {
                queue.splice(0, 1)[0].buffer.close();
                queueByteOffset = 0;
              } else {
                queueByteOffset += srcSize;
              }
            }
          }
        );

      const audioFrame : AudioFrameV2 = {
        sampleOffset,
        sampleCount,
        sampleRate: info.sampleRate,
        channelCount: info.channelCount,
        format: info.format,
        buffer,
      };

      sampleOffset += audioFrame.sampleCount;
      sampleCountInCache -= audioFrame.sampleCount;

      return audioFrame;
    },
    close(): void {
      queue.splice(0).forEach((frame) => frame.buffer.close());
    },
  }
}


function stretchAudioFrame(
  audioFrame: AudioFrameV2,
  outputSampleCount: number,
  bufferCache: BufferCache,
): AudioFrameV2 {
  const audioBufferSize = calculateRawAudioFrameBufferSize(audioFrame, outputSampleCount);

  const buffer =
    bufferCache.allocateBuffer(
      audioBufferSize,
      (buffer: Uint8Array) => {
        switch (audioFrame.format) {
          case 'float32': {
            stretchFloat32Samples(
              audioFrame.buffer.asReadonlyFloat32Array(),
              new Float32Array(buffer.buffer),
              audioFrame.channelCount,
            );
            break;
          }
          case 'pcm16': {
            stretchInt16Samples(
              audioFrame.buffer.asReadonlyInt16Array(),
              new Int16Array(buffer.buffer),
              audioFrame.channelCount,
            );
            break;
          }
        }
      }
    );

  audioFrame.buffer.close();

  return {
    sampleOffset: Math.round(audioFrame.sampleOffset * outputSampleCount / audioFrame.sampleCount),
    sampleCount: outputSampleCount,
    sampleRate: audioFrame.sampleRate,
    channelCount: audioFrame.channelCount,
    format: audioFrame.format,
    buffer,
  }
}

function stretchFloat32Samples(
  src: ReadonlyFloat32Array,
  dst: Float32Array,
  channelCount: number,
): void {
  const dstSampleCount = dst.length / channelCount;
  const srcSampleCount = src.length / channelCount;
  for (let i = 0; i < channelCount; i++) {
    for (let j = 0; j < dstSampleCount - 1; j++) {
      const srcSampleOffset = (j * (srcSampleCount - 1)) / (dstSampleCount - 1);
      const srcSampleOffset0 = Math.floor(srcSampleOffset);
      const srcSampleOffset1 = srcSampleOffset0 + 1;
      const f = srcSampleOffset - srcSampleOffset0;

      dst[(j * channelCount) + i] =
        (src[(srcSampleOffset0 * channelCount) + i] * (1.0 - f)) +
        (src[(srcSampleOffset1 * channelCount) + i] * f);
     }
    dst[((dstSampleCount - 1) * channelCount) + i] = src[((srcSampleCount - 1) * channelCount) + i];
  }
}

function stretchInt16Samples(
  src: ReadonlyInt16Array,
  dst: Int16Array,
  channelCount: number,
): void {
  const dstSampleCount = dst.length / channelCount;
  const srcSampleCount = src.length / channelCount;
  for (let i = 0; i < channelCount; i++) {
    for (let j = 0; j < dstSampleCount - 1; j++) {
      const srcSampleOffset = (j * (srcSampleCount - 1)) / (dstSampleCount - 1);
      const srcSampleOffset0 = Math.floor(srcSampleOffset);
      const srcSampleOffset1 = srcSampleOffset0 + 1;
      const f = srcSampleOffset - srcSampleOffset0;

      dst[(j * channelCount) + i] =
        (src[(srcSampleOffset0 * channelCount) + i] * (1.0 - f)) +
        (src[(srcSampleOffset1 * channelCount) + i] * f);
     }
    dst[((dstSampleCount - 1) * channelCount) + i] = src[((srcSampleCount - 1) * channelCount) + i];
  }
}

